import { useCallback, useEffect } from 'react'
import { WithBodyMessage } from '@weblite/ts-action/message'
import { useHistoryCustomBack, usePanelHistoryTop } from 'weblite-router'
import { useShowAudioPlayer } from '@weblite/audio'
import { PageWithAppBar } from '$common/page-with-app-bar'
import { RoomAppBarSelection } from '$common/room-app-bar'
import { AudioPlayer } from '$common/audio-player'
import { scrollToMessageByIid, useOpenedRoomId } from '$logic/opened-room'
import { closeRoomPins, goBackInRoomPanel } from '$logic/router'
import {
  getMessageByIid,
  openForwardListHandler,
  handleDeleteMessagesModal,
} from '$logic/message'
import { TopBarsContainer } from '$common/top-bars-container'
import { showTextSavedInMemorySnackbar } from '$comp/snackbars'
import { sendFieldApis } from '$comp/_app/components/chat/components/footer/components/send-field'
import { openedRoomMessagesStorage } from '$logic/opened-room/opened-room-messages.storage'
import { editorAPI } from '$comp/_app/components/chat/components/footer/components/send-field/components/text-editor'
import { copyToClipboard } from '$func/clipboard'
import { isPhoneOrTablet } from '$func/environmentInfo'
import { AUDIO_PLAYER_HEIGHT } from '$common/audio-player/audio-player-constants'
import { SelectBar } from '$comp/_app/components/chat/components/select-bar/select-bar'
import { ChatBG } from '$comp/_app/components/chat/components/chat-bg'
import { PinList } from '$comp/_app/components/chat/components/body/components/pin-list/pin-list'
import { t } from '$i18n'
import { AppBarBackTitle } from '$common/app-bar/app-bar-back-title'
import {
  removeRoomAllPinMessages,
  useOpenedRoomPinnedMessagesIids,
} from '$logic/pin'
import { ActionButton } from '$comp/_app/components/chat/components/footer/components/action-button'
import { SelectionContext } from '$comp/_app/components/chat/room'
import { FOOTER_ACTION_BUTTON_HEIGHT } from '$comp/_app/components/chat/components/conf'
import {
  useActionsPermissions,
  useFooterHeight,
  useMultiSelect,
} from './react-hooks'
import {
  useClientUserPermissions,
  useClientUserPermissionsSync,
} from '$logic/subscription/react-hook'
import { isHall, useRoomAttribute } from '$logic/room'

const SELECT_BAR_HEIGHT = 54

// TODO: Refactor hooks -> extract some custom hooks to decrease file size
export function RoomPins() {
  const panel = usePanelHistoryTop()?.state

  const roomId = useOpenedRoomId()
  const roomType = useRoomAttribute('type', roomId)
  const isRoomTypeHall = isHall(roomType)
  const {
    selectionMap,
    setSelection,
    deleteSelection,
    resetSelection,
    hasSelection,
    selectedItems,
  } = useMultiSelect()

  const {
    canEdit: canEditSelectedMessages,
    canCopy: canCopySelectedMessages,
    canDelete: canDeleteSelectedMessages,
    canDeleteWapps: canDeleteWappsWithinSelectedMessages,
    canForward: canForwardSelectedMessages,
    protectContent,
    arePermissionsStale,
  } = useActionsPermissions(roomId, selectedItems)

  const clientUserPermissionsInHall = useClientUserPermissionsSync(roomId)

  const openSelectBar = canForwardSelectedMessages && hasSelection

  const showRemoveAllPinsButton =
    (!isRoomTypeHall || clientUserPermissionsInHall?.isAdmin) && !openSelectBar

  const { showAudioPlayer } = useShowAudioPlayer()

  const toggleSelection = useCallback(
    (selected: boolean) => (selected ? deleteSelection : setSelection),
    [],
  )

  const FOOTER_HEIGHT = 0 // NOTE: WhenEver a separate sendField becomes implemented
  // a hook should be added to update this value

  useHistoryCustomBack(() => {
    if (hasSelection) resetSelection()
    else goBackInRoomPanel()
  }, [hasSelection])

  const exitRoomFromSelectionMode = () => resetSelection()

  // NOTE: Deselect items when user clicked back button
  useEffect(() => {
    if (panel?.roomId && !isPhoneOrTablet) editorAPI.focus()
  }, [panel])

  const handleForward = useCallback(async () => {
    await exitRoomFromSelectionMode()
    await openForwardListHandler(roomId, selectedItems)
  }, [selectedItems, roomId])

  const handleEdit = useCallback(async () => {
    await exitRoomFromSelectionMode()
    const [iidToEdit] = selectedItems
    const message = await openedRoomMessagesStorage.get(iidToEdit)
    closeRoomPins()
    scrollToMessageByIid(iidToEdit)
    if (message) sendFieldApis.setEditContent(message as WithBodyMessage)
  }, [selectedItems])

  const handleCopy = useCallback(async () => {
    await exitRoomFromSelectionMode()
    const messages = (await Promise.all(
      selectedItems.map(openedRoomMessagesStorage.get),
    )) as WithBodyMessage[]

    const messageBodies = messages.map(({ body }) => body).filter(Boolean)
    copyToClipboard(messageBodies.join('\n\n'))
    showTextSavedInMemorySnackbar()
  }, [selectedItems])

  const handleDelete = useCallback(async () => {
    await exitRoomFromSelectionMode()
    const messages = await Promise.all(
      selectedItems.map(item => getMessageByIid(roomId, item)),
    )

    handleDeleteMessagesModal(
      messages,
      canDeleteWappsWithinSelectedMessages,
      roomId,
    )
  }, [selectedItems, roomId, canDeleteWappsWithinSelectedMessages])

  const topBarsHeight = showAudioPlayer ? AUDIO_PLAYER_HEIGHT : 0

  const openedRoomPinMessageIids = useOpenedRoomPinnedMessagesIids()

  return (
    <PageWithAppBar
      appBar={
        hasSelection ? (
          <RoomAppBarSelection
            selectionCount={selectedItems.length}
            canCopy={canCopySelectedMessages}
            canEdit={canEditSelectedMessages}
            canDelete={canDeleteSelectedMessages}
            canForward={canForwardSelectedMessages}
            arePermissionsStale={arePermissionsStale}
            onForward={handleForward}
            onCopy={handleCopy}
            onDelete={handleDelete}
            onEdit={handleEdit}
            onBack={resetSelection}
          />
        ) : (
          <AppBarBackTitle
            title={t('roomPins.pinned')(openedRoomPinMessageIids.length)}
            onBack={closeRoomPins}
          />
        )
      }
      rootSx={{ position: 'relative' }}
      bodySx={{
        height: '100%',
        backgroundColor: '#ebebeb',
        position: 'relative',
      }}
    >
      <ChatBG />

      <TopBarsContainer isStatic hasAnyChild={showAudioPlayer}>
        {showAudioPlayer && <AudioPlayer />}
      </TopBarsContainer>

      <div
        style={{
          position: 'relative',
          width: '100%',
          height: `calc(100% - ${
            openSelectBar
              ? SELECT_BAR_HEIGHT + topBarsHeight
              : showRemoveAllPinsButton
                ? FOOTER_ACTION_BUTTON_HEIGHT + topBarsHeight
                : FOOTER_HEIGHT + topBarsHeight
          }px)`,
          flexShrink: 0,
        }}
      >
        <SelectionContext.Provider value={selectionMap}>
          <PinList toggleSelection={toggleSelection} />
        </SelectionContext.Provider>
      </div>

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          height: '100%',
        }}
      >
        {openSelectBar && (
          <SelectBar
            protectContent={protectContent}
            onForward={canForwardSelectedMessages ? handleForward : null}
            onReply={null}
            arePermissionsStale={arePermissionsStale}
          />
        )}

        {showRemoveAllPinsButton && (
          <ActionButton
            text={t('roomPins.removeAllPins')}
            onClick={async () => {
              await removeRoomAllPinMessages(roomId)
              closeRoomPins()
            }}
          />
        )}
      </div>
    </PageWithAppBar>
  )
}
